import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin auth is now cookie-based (httpOnly, Secure, SameSite=Strict, path=/api).
 * The JWT is never exposed to JavaScript — the browser stores it in a cookie
 * only the server can read. This eliminates the XSS token-theft vector that
 * plain localStorage had.
 *
 * All requests use `withCredentials: true` so the cookie is sent
 * automatically. There is no client-side token to manage.
 */
export const authClient = () =>
  axios.create({
    baseURL: API,
    withCredentials: true,
  });

export const adminLogin = async (password) => {
  const { data } = await axios.post(
    `${API}/admin/login`,
    { password },
    { withCredentials: true },
  );
  return data;
};

export const adminLogout = async () => {
  try {
    await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
  } catch (_e) {
    void _e;
    /* logout is best-effort; cookie will expire on its own */
  }
};

/**
 * Ping /admin/me to check whether the httpOnly cookie is still valid.
 * Returns `true` on 200, `false` on 401/403 (or network error).
 * Replaces the old `adminGetToken()` presence check.
 */
export const adminCheckAuth = async () => {
  try {
    await authClient().get("/admin/me");
    return true;
  } catch {
    return false;
  }
};

export const fetchLeads = async () => (await authClient().get("/leads")).data;
export const fetchQuizResults = async () =>
  (await authClient().get("/quiz-results")).data;
export const fetchNurtureSummary = async () =>
  (await authClient().get("/admin/nurture/summary")).data;
export const fetchBookings = async () =>
  (await authClient().get("/bookings")).data;
export const fetchNewsletterAudience = async () =>
  (await authClient().get("/admin/newsletter/audience")).data;
export const broadcastNewsletter = async ({ subject, html_body, segment }) =>
  (await authClient().post("/admin/newsletter/broadcast", {
    subject,
    html_body,
    segment: segment || null,
  })).data;

export const scheduleNewsletter = async ({ subject, html_body, segment, send_at }) =>
  (await authClient().post("/admin/newsletter/schedule", {
    subject,
    html_body,
    segment: segment || null,
    send_at,
  })).data;

export const fetchScheduledNewsletters = async () =>
  (await authClient().get("/admin/newsletter/scheduled")).data;

export const cancelScheduledNewsletter = async (id) =>
  (await authClient().delete(`/admin/newsletter/scheduled/${id}`)).data;

export const fetchCPA = async () =>
  (await authClient().get("/admin/cpa")).data;

export const updateCPA = async (payload) =>
  (await authClient().post("/admin/cpa", payload)).data;

export const fetchMetaAds = async () =>
  (await authClient().get("/admin/meta-ads")).data;

export const updateMetaSpend = async (payload) =>
  (await authClient().post("/admin/meta-ads/spend", payload)).data;

export const fetchTravelAds = async () =>
  (await authClient().get("/admin/travel-ads")).data;

export const fetchVoiceWidgetReport = async () =>
  (await authClient().get("/admin/ab/voice-widget")).data;

export const updateTravelAds = async (payload) =>
  (await authClient().post("/admin/travel-ads", payload)).data;

export const requestPasswordReset = async () => {
  const { data } = await axios.post(`${API}/admin/password-reset/request`, {});
  return data;
};

export const confirmPasswordReset = async (token, newPassword) => {
  const { data } = await axios.post(`${API}/admin/password-reset/confirm`, {
    token,
    new_password: newPassword,
  });
  return data;
};
